import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import {
	fireTrackAnalytics,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { useAddIssuesToVersionMutation as useAddIssuesToVersionMutationType } from '@atlassian/jira-relay/src/__generated__/useAddIssuesToVersionMutation.graphql';
import {
	createBoardAri,
	createIssueAris,
} from '@atlassian/jira-software-version-backlog-panel-relay/src/common/utils/ari/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { CardId } from '../../../model/types.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import { getRapidViewId } from '../../../state/selectors/app/index.tsx';
import { useOnIssueVersionsUpdated } from '../../../state/state-hooks/use-on-issue-versions-updated.tsx';
import { ANALYTICS_SOURCE_BACKLOG } from '../../constants/analytics.tsx';
import messages from '../messages.tsx';

export const useAddIssuesToVersionMutation = () => {
	const cloudId = useCloudId();
	const boardId = useBacklogSelector((state) => Number(getRapidViewId(state)));
	const boardAri = createBoardAri(cloudId, boardId);

	const onIssueVersionsUpdated = useOnIssueVersionsUpdated();
	const { showFlag } = useFlagsService();

	const [commitFn] = useMutation<useAddIssuesToVersionMutationType>(graphql`
		mutation useAddIssuesToVersionMutation($input: JiraAddIssuesToFixVersionInput!, $boardId: ID!) {
			jira {
				addIssuesToFixVersion(input: $input) {
					success
					version {
						# We want to fetch and update the fields that are used by the version panel if its open
						...versionCard_softwareVersionBacklogPanelRelay_jiraVersionFragment
					}
				}
			}
		}
	`);

	const addIssuesToVersionCallback = useCallback(
		(issueIds: CardId[], versionAri: string, analyticsEvent: UIAnalyticsEvent) => {
			commitFn({
				variables: {
					input: {
						issueIds: createIssueAris(cloudId, issueIds),
						versionId: versionAri,
					},
					boardId: boardAri,
				},
				onCompleted: () => {
					// Request card data refresh for redux state and detail view if its open
					onIssueVersionsUpdated(issueIds);

					fireTrackAnalytics(analyticsEvent, 'field updated', {
						analyticsSource: ANALYTICS_SOURCE_BACKLOG,
						operation: 'addIssuesToVersion',
						versionAri,
						isContextualEditing: true,
					});
				},
				onError: (error) => {
					showFlag({
						type: 'error',
						title: messages.versionUpdateErrorTitle,
						description: messages.versionUpdateErrorDescription,
					});

					log.safeErrorWithoutCustomerData(
						'jiraSoftwareContextMenu.jira.backlog.version.addVersion',
						'An error occurred when adding a version to issues in AGG',
						error,
					);
					fireOperationalAnalytics(analyticsEvent, 'field updateFailed', {
						analyticsSource: ANALYTICS_SOURCE_BACKLOG,
						operation: 'addIssuesToVersion',
						error,
						versionAri,
						isContextualEditing: true,
					});
				},
			});
		},
		[cloudId, boardAri, commitFn, onIssueVersionsUpdated, showFlag],
	);

	return addIssuesToVersionCallback;
};
